import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import '../styles/Login.css';

const SetCredentials = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { email, verificationToken } = location.state || {};

  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isRepeatPasswordVisible, setIsRepeatPasswordVisible] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Quay lại register nếu thiếu token (vd. user truy cập trực tiếp)
  useEffect(() => {
    if (!verificationToken || !email) {
      navigate('/register');
    }
  }, [verificationToken, email, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!fullName.trim()) {
      setError('Vui lòng nhập họ và tên.');
      return;
    }
    if (password.length < 8) {
      setError('Mật khẩu phải có ít nhất 8 ký tự.');
      return;
    }
    if (password !== repeatPassword) {
      setError('Mật khẩu nhập lại không khớp.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/client/auth/register/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'accept': 'application/json' },
        body: JSON.stringify({
          verification_token: verificationToken,
          full_name: fullName.trim(),
          password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        const detail = errorData.detail;
        const msg = Array.isArray(detail)
          ? detail.map((d) => d.msg || JSON.stringify(d)).join('; ')
          : (typeof detail === 'string' ? detail : 'Tạo tài khoản thất bại. Vui lòng thử lại.');
        throw new Error(msg);
      }

      setSuccess('Đăng ký thành công! Đang chuyển đến trang đăng nhập...');
      setTimeout(() => navigate('/login'), 1200);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const inputClass = "w-full pl-12 pr-4 py-4 bg-hp-surface-container-low border-none rounded-xl focus:ring-2 focus:ring-hupath-scout/30 transition-all font-body text-hp-on-surface placeholder:text-hp-outline";
  const inputClassPassword = "w-full pl-12 pr-12 py-4 bg-hp-surface-container-low border-none rounded-xl focus:ring-2 focus:ring-hupath-scout/30 transition-all font-body text-hp-on-surface placeholder:text-hp-outline";

  return (
    <div className="min-h-screen flex items-stretch overflow-hidden bg-hp-surface font-body text-hp-on-surface">
      {/* ── LEFT HERO PANEL ── */}
      <section className="hidden lg:flex lg:w-3/5 hp-hero-gradient--register relative flex-col justify-between p-12 overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="bg-white/90 backdrop-blur-md p-2 rounded-xl shadow-sm">
              <img src="/logo_simple.png" alt="HuPath Logo" className="w-10 h-10 object-contain" />
            </div>
            <h1 className="font-headline font-black text-3xl text-hupath-owl tracking-tight">HuPath</h1>
          </div>
        </div>

        <div className="relative z-10 max-w-2xl mb-12">
          <div className="inline-flex items-center gap-2 bg-white/30 backdrop-blur-xl px-4 py-2 rounded-full mb-6 text-hupath-owl font-headline font-bold text-sm">
            <span className="material-symbols-outlined text-sm">check_circle</span>
            Bước cuối cùng
          </div>
          <h2 className="font-headline text-5xl xl:text-6xl font-extrabold text-hupath-ink leading-[1.1] mb-6">
            Hoàn tất hồ sơ của em! ✨
          </h2>
          <p className="font-body text-xl text-hupath-ink/80 leading-relaxed max-w-lg italic">
            Đặt tên hiển thị và mật khẩu để bắt đầu hành trình học tiếng Anh cùng HuPath.
          </p>
        </div>

        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 right-1/4 w-48 h-24 bg-white/40 blur-2xl rounded-full opacity-60"></div>
          <div className="absolute bottom-1/3 left-1/4 w-64 h-32 bg-hupath-gold/30 blur-3xl rounded-full opacity-50"></div>
        </div>
      </section>

      {/* ── RIGHT FORM PANEL ── */}
      <main className="w-full lg:w-2/5 bg-white flex items-center justify-center p-6 md:p-12 relative overflow-y-auto">
        <div className="w-full max-w-md relative z-10">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <img src="/logo_simple.png" alt="HuPath Logo" className="w-8 h-8 object-contain" />
            <span className="font-headline font-bold text-xl text-hupath-scout">HuPath</span>
          </div>

          <div className="mb-8">
            <h3 className="font-headline text-3xl font-bold text-hp-on-surface mb-3">Tạo tài khoản</h3>
            <p className="font-body text-base text-hp-on-surface-variant">
              Đặt họ tên và mật khẩu cho{' '}
              <span className="font-semibold text-hupath-scout">{email}</span>
            </p>
          </div>

          <form className="space-y-4" onSubmit={handleSubmit}>
            {/* Full Name */}
            <div className="space-y-2">
              <label className="text-sm font-medium font-headline text-hp-on-surface-variant ml-1" htmlFor="fullName">Họ và tên</label>
              <div className="relative flex items-center group">
                <span className="material-symbols-outlined absolute left-4 text-hp-outline group-focus-within:text-hupath-scout transition-colors">person</span>
                <input id="fullName" type="text" placeholder="Nhập họ và tên của em" value={fullName}
                  onChange={(e) => setFullName(e.target.value)} required className={inputClass} />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-2">
              <label className="text-sm font-medium font-headline text-hp-on-surface-variant ml-1" htmlFor="password">Mật khẩu</label>
              <div className="relative flex items-center group">
                <span className="material-symbols-outlined absolute left-4 text-hp-outline group-focus-within:text-hupath-scout transition-colors">lock</span>
                <input id="password" type={isPasswordVisible ? 'text' : 'password'} placeholder="Ít nhất 8 ký tự" value={password}
                  onChange={(e) => setPassword(e.target.value)} required className={inputClassPassword} />
                <button type="button" onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                  className="absolute right-4 text-hp-outline hover:text-hupath-scout transition-colors" aria-label="Toggle password">
                  <span className="material-symbols-outlined">{isPasswordVisible ? 'visibility' : 'visibility_off'}</span>
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <label className="text-sm font-medium font-headline text-hp-on-surface-variant ml-1" htmlFor="repeatPassword">Xác nhận mật khẩu</label>
              <div className="relative flex items-center group">
                <span className="material-symbols-outlined absolute left-4 text-hp-outline group-focus-within:text-hupath-scout transition-colors">lock</span>
                <input id="repeatPassword" type={isRepeatPasswordVisible ? 'text' : 'password'} placeholder="Nhập lại mật khẩu" value={repeatPassword}
                  onChange={(e) => setRepeatPassword(e.target.value)} required className={inputClassPassword} />
                <button type="button" onClick={() => setIsRepeatPasswordVisible(!isRepeatPasswordVisible)}
                  className="absolute right-4 text-hp-outline hover:text-hupath-scout transition-colors" aria-label="Toggle repeat password">
                  <span className="material-symbols-outlined">{isRepeatPasswordVisible ? 'visibility' : 'visibility_off'}</span>
                </button>
              </div>
            </div>

            {/* Error / Success */}
            {error && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-300 rounded-xl px-4 py-3 text-red-600 text-sm font-medium">
                <span className="material-symbols-outlined text-lg">error</span>{error}
              </div>
            )}
            {success && (
              <div className="flex items-center gap-2 bg-green-50 border border-green-300 rounded-xl px-4 py-3 text-green-600 text-sm font-medium">
                <span className="material-symbols-outlined text-lg">check_circle</span>{success}
              </div>
            )}

            {/* Submit */}
            <button type="submit" disabled={isLoading}
              className="w-full hp-button-gradient text-white font-headline font-bold text-lg py-4 rounded-xl shadow-[0px_8px_24px_rgba(42,107,48,0.25)] hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100">
              {isLoading ? (
                <><span className="material-symbols-outlined lf-spin text-xl">progress_activity</span> Đang tạo tài khoản...</>
              ) : 'Hoàn tất đăng ký'}
            </button>
          </form>

          {/* Login Link */}
          <div className="mt-8 text-center">
            <p className="text-hp-on-surface-variant font-body">
              Đã có tài khoản?{' '}
              <Link to="/login" className="text-hupath-scout font-bold font-headline ml-1 hover:underline">Đăng nhập</Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SetCredentials;
